import React, { useState, useCallback } from 'react';
import { useTelegram } from './TelegramProvider';
import { useTon } from './TelegramTonProvider';
import { useGameBalance } from './hooks/useGameBalance';
import { useMultiplier } from './hooks/useMultiplier';
import { useSounds } from './hooks/useSounds';
import { formatLilape } from './config/gameConfig';
import { DepositWithdrawPanel } from './components/DepositWithdrawPanel';

const BET_OPTIONS = [20_000_000, 50_000_000, 100_000_000, 500_000_000];
const DICE_FACES = ['⚀','⚁','⚂','⚃','⚄','⚅'];
const WIN_RATE = 0.03; // 3% win rate
const WIN_MULT = 10; // x10 base (x30 with Gold VIP)

export function DiceGame({ onBack }) {
  const { hapticFeedback } = useTelegram();
  const { connected, sendTransaction, address } = useTon();
  const gameBalanceHook = useGameBalance(address, sendTransaction);
  const { gameBalance, useSpin, addWinnings, canSpin } = gameBalanceHook;
  const { applyMultiplier } = useMultiplier();
  const { play, spinSound } = useSounds();

  const [bet, setBet] = useState(BET_OPTIONS[0]);
  const [guess, setGuess] = useState(1);
  const [rolling, setRolling] = useState(false);
  const [result, setResult] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [winAmt, setWinAmt] = useState(0);
  const [disp, setDisp] = useState(1);
  const [showWallet, setShowWallet] = useState(false);

  const roll = useCallback(async () => {
    if (rolling) return;
    if (!canSpin(bet)) {
      hapticFeedback?.('error');
      return;
    }

    const betSuccess = await useSpin(bet, 'dice');
    if (!betSuccess) return;

    hapticFeedback?.('medium');
    spinSound();
    setRolling(true);
    setResult(null);
    setOutcome(null);

    let t = 0;
    const iv = setInterval(() => {
      setDisp(Math.ceil(Math.random() * 6));
      t++;
      if (t > 15) clearInterval(iv);
    }, 80);

    setTimeout(() => {
      clearInterval(iv);
      // 3% win rate - ignore actual dice result
      const won = Math.random() < WIN_RATE;
      const r = won ? guess : (guess % 6) + 1; // Show matching or different number
      setDisp(r);
      setResult(r);
      const baseGain = won ? Math.round(bet * WIN_MULT) : 0;
      const gain = applyMultiplier(baseGain);

      if (gain > 0) {
        addWinnings(gain, 'dice', bet);
      }

      setOutcome(won ? 'win' : 'lose');
      setWinAmt(gain);
      play(won ? 'bigWin' : 'lose');
      setRolling(false);
      hapticFeedback?.(won ? 'heavy' : 'error');
    }, 1300);
  }, [rolling, bet, guess, canSpin, useSpin, addWinnings, applyMultiplier, hapticFeedback, play, spinSound]);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 100%)',
      padding: '16px',
      paddingBottom: '100px',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
        <button onClick={onBack} style={{
          background: 'rgba(255,255,255,0.1)',
          border: 'none',
          borderRadius: '12px',
          padding: '10px 16px',
          color: '#fff',
          fontSize: '16px',
          cursor: 'pointer',
        }}>← Back</button>
        <h1 style={{
          flex: 1,
          textAlign: 'center',
          color: '#FF6B6B',
          fontSize: '24px',
          margin: 0,
        }}>🎲 Dice Roll</h1>
      </div>

      {/* Balance */}
      <div style={{
        background: 'rgba(255,107,107,0.1)',
        border: '1px solid rgba(255,107,107,0.3)',
        borderRadius: '16px',
        padding: '16px',
        textAlign: 'center',
        marginBottom: '20px',
      }}>
        <div style={{ color: '#888', fontSize: '12px' }}>GAME BALANCE</div>
        <div style={{ color: '#FF6B6B', fontSize: '28px', fontWeight: 'bold', marginBottom: 8 }}>
          {formatLilape(gameBalance)} LILAPE
        </div>
        <button onClick={() => setShowWallet(true)} style={{
          padding: '8px 20px', borderRadius: 8,
          background: 'linear-gradient(135deg, #10B981, #059669)',
          border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
        }}>💰 Deposit / Withdraw</button>
      </div>

      {showWallet && <DepositWithdrawPanel onClose={() => setShowWallet(false)} gameBalanceHook={gameBalanceHook} />}

      {/* Dice Display */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '24px',
      }}>
        <div style={{
          width: '120px',
          height: '120px',
          background: 'linear-gradient(135deg, #fff, #eee)',
          borderRadius: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '72px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          animation: rolling ? 'shake 0.1s infinite' : 'none',
        }}>
          {DICE_FACES[disp - 1]}
        </div>
      </div>

      {/* Result */}
      {outcome && (
        <div style={{
          textAlign: 'center',
          marginBottom: '20px',
          padding: '16px',
          borderRadius: '12px',
          background: outcome === 'win' ? 'rgba(0,255,0,0.1)' : 'rgba(255,0,0,0.1)',
        }}>
          <div style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: outcome === 'win' ? '#00ff00' : '#ff4444',
          }}>
            {outcome === 'win' ? `🎉 WIN +${formatLilape(winAmt)}` : '😢 LOSE'}
          </div>
        </div>
      )}

      {/* Guess Selection */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ color: '#888', fontSize: '12px', marginBottom: '8px', textAlign: 'center' }}>
          GUESS THE NUMBER (x5.7 payout)
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
          {[1,2,3,4,5,6].map(n => (
            <button
              key={n}
              onClick={() => !rolling && setGuess(n)}
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                border: guess === n ? '2px solid #FF6B6B' : '1px solid rgba(255,255,255,0.2)',
                background: guess === n ? 'rgba(255,107,107,0.2)' : 'rgba(255,255,255,0.05)',
                color: '#fff',
                fontSize: '24px',
                cursor: 'pointer',
                opacity: rolling ? 0.5 : 1,
              }}
            >
              {DICE_FACES[n - 1]}
            </button>
          ))}
        </div>
      </div>

      {/* Bet Options */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ color: '#888', fontSize: '12px', marginBottom: '8px', textAlign: 'center' }}>
          BET AMOUNT
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {BET_OPTIONS.map(opt => (
            <button
              key={opt}
              onClick={() => !rolling && setBet(opt)}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: bet === opt ? '2px solid #FF6B6B' : '1px solid rgba(255,255,255,0.2)',
                background: bet === opt ? 'rgba(255,107,107,0.2)' : 'rgba(255,255,255,0.05)',
                color: bet === opt ? '#FF6B6B' : '#fff',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                opacity: rolling ? 0.5 : 1,
              }}
            >
              {formatLilape(opt)}
            </button>
          ))}
        </div>
      </div>

      {/* Roll Button */}
      <button
        onClick={roll}
        disabled={rolling || !canSpin(bet)}
        style={{
          width: '100%',
          padding: '18px',
          borderRadius: '14px',
          border: 'none',
          background: rolling ? '#555' : canSpin(bet) ? 'linear-gradient(135deg, #FF6B6B, #FF8E53)' : '#333',
          color: '#fff',
          fontSize: '18px',
          fontWeight: 'bold',
          cursor: rolling || !canSpin(bet) ? 'not-allowed' : 'pointer',
        }}
      >
        {rolling ? '🎲 ROLLING...' : `ROLL ${formatLilape(bet)}`}
      </button>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: rotate(-3deg); }
          50% { transform: rotate(3deg); }
        }
      `}</style>
    </div>
  );
}
